/**
 * 表示の丸め（docs/spec/complexity-meter.md §6）。テキストとしての正本は `10^k`。
 */

const grouping = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0, useGrouping: true });

/** 整数に丸めて 3 桁ごとに `,`。 */
export function groupDigits(x: number): string {
  return grouping.format(Math.round(x));
}

/**
 * x < 10^5: 整数 3 桁区切り。x ≥ 10^5: `m×10^k`（m は小数第 1 位、m = 1 なら `10^k`）。
 */
export function formatCount(x: number): string {
  if (!Number.isFinite(x)) return String(x);
  if (x < 0) return '-' + formatCount(-x);
  const r = Math.round(x);
  if (r < 1e5) return groupDigits(r);
  let k = Math.floor(Math.log10(x));
  let m = x / 10 ** k;
  if (m >= 10) {
    k += 1;
    m /= 10;
  } else if (m < 1) {
    k -= 1;
    m *= 10;
  }
  m = Math.round(m * 10) / 10;
  if (m >= 10) {
    m = 1;
    k += 1;
  }
  return m === 1 ? `10^${k}` : `${m}×10^${k}`;
}

/** 見積もりの秒数（§6 formatSeconds）。 */
export function formatSeconds(s: number): string {
  if (s < 0.01) return '0.01 秒未満';
  if (s < 10) return `${Number(s.toPrecision(2))} 秒`;
  if (s < 60) return `約 ${Math.round(s)} 秒`;
  if (s < 3600) return `約 ${Math.round(s / 60)} 分`;
  if (s < 86400) return `約 ${Math.round(s / 3600)} 時間`;
  if (s < 365 * 86400) return `約 ${Math.round(s / 86400)} 日`;
  return `約 ${formatCount(Math.round(s / (365 * 86400)))} 年`;
}

/** 実測ステップ数（§6）。常に 3 桁区切り、10^5 以上は `（formatCount）` を添え、下限なら `≥ `。 */
export function formatTicks(ticks: number, lowerBound = false): string {
  const head = lowerBound ? '≥ ' : '';
  const body = groupDigits(ticks);
  return ticks >= 1e5 ? `${head}${body}（${formatCount(ticks)}）` : head + body;
}

/** 実行時間（§6 durationMs）。1,000 未満は `{整数} ms`、以上は秒の小数第 2 位、timedOut は `3 秒超`。 */
export function formatDurationMs(ms: number, timedOut = false): string {
  if (timedOut) return '3 秒超';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} 秒`;
}

/** 比の表示（§6 伸び率）。10 未満は小数第 1 位、10 以上は整数。 */
export function formatRatio(r: number): string {
  return r < 10 ? r.toFixed(1) : String(Math.round(r));
}

function formatSizeRatio(r: number): string {
  return Number.isInteger(r) ? String(r) : formatRatio(r);
}

/** 隣り合う 2 点の伸び率（§6）。`N を 2 倍にすると回数は 4.0 倍`。t1 が 0 なら null。 */
export function formatGrowth(n1: number, t1: number, n2: number, t2: number): string | null {
  if (t1 <= 0 || n1 <= 0) return null;
  return `N を ${formatSizeRatio(n2 / n1)} 倍にすると回数は ${formatRatio(t2 / t1)} 倍`;
}

export interface GrowthExponent {
  /** 小数第 1 位に丸めた指数。 */
  exponent: number;
  /** 整数との差が 0.15 以内ならその整数。 */
  approx: number | null;
  display: string;
}

/**
 * 伸び方の指数（§6）。ticks > 0 の点のうち N が大きい 2 点で log(t2/t1) / log(N2/N1)。
 * 点が 2 つ未満なら null。
 */
export function growthExponent(
  points: readonly { n: number; ticks: number | null }[],
): GrowthExponent | null {
  const usable = points
    .filter((p): p is { n: number; ticks: number } => p.ticks !== null && p.ticks > 0 && p.n > 0)
    .sort((a, b) => a.n - b.n);
  const p2 = usable[usable.length - 1];
  const p1 = usable[usable.length - 2];
  if (!p1 || !p2 || p1.n === p2.n) return null;
  const raw = Math.log(p2.ticks / p1.ticks) / Math.log(p2.n / p1.n);
  const exponent = Math.round(raw * 10) / 10;
  const nearest = Math.round(raw);
  const approx = Math.abs(raw - nearest) <= 0.15 && nearest >= 0 ? nearest : null;
  let display = `N の ${exponent.toFixed(1)} 乗`;
  if (approx !== null) {
    const label = approx === 0 ? '一定' : approx === 1 ? 'N' : `N^${approx}`;
    display += `（≈ ${label}）`;
  }
  return { exponent, approx, display };
}
